import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ResearchShell from '../components/research/ResearchShell'
import { SECTORS, type DivergenceDirection, type ScreenerResult, type VolatilityProfile } from '../types/research'
import { screenVolatility, isResearchUsingMockData } from '../lib/research-api'

function fmtMcap(v: number): string {
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`
  if (v >= 1e9) return `$${(v / 1e9).toFixed(0)}B`
  return `$${(v / 1e6).toFixed(0)}M`
}

function DivergenceCell({ p }: { p: VolatilityProfile }) {
  const rich = p.volDivergence > 0
  return (
    <span
      className={`font-semibold ${
        rich ? 'text-amber-600 dark:text-amber-400' : 'text-sky-600 dark:text-sky-400'
      }`}
    >
      {rich ? '+' : ''}
      {p.volDivergence}
    </span>
  )
}

function ResultsTable({ rows, onFlag }: { rows: VolatilityProfile[]; onFlag: (p: VolatilityProfile) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-200 dark:border-slate-700">
            <th className="py-2 pr-3 font-medium">Symbol</th>
            <th className="py-2 pr-3 font-medium hidden md:table-cell">Sector</th>
            <th className="py-2 pr-3 font-medium text-right">Price</th>
            <th className="py-2 pr-3 font-medium text-right" title="30-day realized volatility, annualized">RV 30d</th>
            <th className="py-2 pr-3 font-medium text-right" title="~30-day at-the-money implied volatility">IV 30d</th>
            <th className="py-2 pr-3 font-medium text-right" title="Implied minus realized, vol points">Diverg.</th>
            <th className="py-2 pr-3 font-medium text-right hidden sm:table-cell" title="Implied / realized">Ratio</th>
            <th className="py-2 pr-3 font-medium text-right hidden lg:table-cell">Mkt cap</th>
            <th className="py-2 pr-3 font-medium hidden sm:table-cell">Earnings</th>
            <th className="py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/50">
          {rows.map((p) => (
            <tr key={p.symbol} className="hover:bg-slate-100/70 dark:hover:bg-slate-700/20 transition-colors">
              <td className="py-2.5 pr-3">
                <Link
                  to={`/dashboard/financial-app/fundamentals/${p.symbol}`}
                  className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {p.symbol}
                </Link>
                <span className="block text-xs text-slate-500 max-w-[160px] truncate">{p.name}</span>
              </td>
              <td className="py-2.5 pr-3 text-slate-500 dark:text-slate-400 hidden md:table-cell">{p.sector}</td>
              <td className="py-2.5 pr-3 text-right text-slate-700 dark:text-slate-200">${p.price.toFixed(2)}</td>
              <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300">{p.realizedVol30d}%</td>
              <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300">{p.impliedVol30d}%</td>
              <td className="py-2.5 pr-3 text-right"><DivergenceCell p={p} /></td>
              <td className="py-2.5 pr-3 text-right text-slate-600 dark:text-slate-300 hidden sm:table-cell">{p.divergenceRatio}×</td>
              <td className="py-2.5 pr-3 text-right text-slate-500 dark:text-slate-400 hidden lg:table-cell">{fmtMcap(p.marketCap)}</td>
              <td className="py-2.5 pr-3 hidden sm:table-cell">
                {p.nextEarningsDate ? (
                  <span className={`text-xs ${(p.daysUntilEarnings ?? 99) <= 14 ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-slate-500'}`}>
                    {p.nextEarningsDate}
                    {p.daysUntilEarnings != null && ` (${p.daysUntilEarnings}d)`}
                  </span>
                ) : (
                  <span className="text-xs text-slate-500">—</span>
                )}
              </td>
              <td className="py-2.5 text-right whitespace-nowrap">
                <Link
                  to={`/dashboard/financial-app/options/${p.symbol}`}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline mr-3"
                >
                  Options
                </Link>
                <button
                  onClick={() => onFlag(p)}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  title="Open in Opus Research with this symbol pre-filled"
                >
                  Ask Opus
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="text-center text-sm text-slate-500 py-8">No equities match the current criteria.</p>
      )}
    </div>
  )
}

export default function ScreenerPage() {
  const navigate = useNavigate()
  const [sectors, setSectors] = useState<string[]>([])
  const [direction, setDirection] = useState<DivergenceDirection>('any')
  const [minDivergence, setMinDivergence] = useState(4)
  const [groupBySector, setGroupBySector] = useState(false)
  const [sortBy, setSortBy] = useState<'divergence' | 'ratio' | 'impliedVol' | 'marketCap' | 'earnings'>('divergence')
  const [result, setResult] = useState<ScreenerResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [isMock, setIsMock] = useState(false)

  async function run() {
    setLoading(true)
    try {
      const r = await screenVolatility({
        sectors: sectors.length ? sectors : undefined,
        direction,
        minDivergence: minDivergence || undefined,
        sortBy,
        groupBySector,
        limit: groupBySector ? 5 : 40,
      })
      setResult(r)
      setIsMock(isResearchUsingMockData())
    } finally {
      setLoading(false)
    }
  }

  // Initial run + re-run when criteria change
  useEffect(() => {
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectors, direction, minDivergence, groupBySector, sortBy])

  function toggleSector(s: string) {
    setSectors((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))
  }

  function askOpus(p: VolatilityProfile) {
    navigate(`/dashboard/financial-app/research?task=analyze-security&symbol=${p.symbol}`)
  }

  return (
    <ResearchShell showMockBanner={isMock && !loading}>
      {/* Criteria */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Short-term volatility divergence screen
          </h2>
          <span className="text-xs text-slate-500 hidden sm:block">
            Divergence = 30d implied vol − 30d realized vol
          </span>
        </div>

        {/* Sector chips */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          <button
            onClick={() => setSectors([])}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              sectors.length === 0
                ? 'bg-blue-600 text-white'
                : 'bg-slate-200/70 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            All sectors
          </button>
          {SECTORS.map((s) => (
            <button
              key={s}
              onClick={() => toggleSector(s)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                sectors.includes(s)
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-200/70 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-xs text-slate-500 dark:text-slate-400">Direction</span>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value as DivergenceDirection)}
              className="mt-1 w-full bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2.5 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
            >
              <option value="any">Rich or cheap</option>
              <option value="rich">Rich (IV &gt; RV)</option>
              <option value="cheap">Cheap (IV &lt; RV)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 dark:text-slate-400">Min divergence (vol pts)</span>
            <input
              type="number"
              min={0}
              max={30}
              value={minDivergence}
              onChange={(e) => setMinDivergence(Number(e.target.value))}
              className="mt-1 w-full bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2.5 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 dark:text-slate-400">Sort by</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="mt-1 w-full bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-2.5 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
            >
              <option value="divergence">Divergence</option>
              <option value="ratio">IV/RV ratio</option>
              <option value="impliedVol">Implied vol</option>
              <option value="marketCap">Market cap</option>
              <option value="earnings">Next earnings</option>
            </select>
          </label>
          <label className="flex items-end gap-2 pb-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={groupBySector}
              onChange={(e) => setGroupBySector(e.target.checked)}
              className="w-4 h-4 accent-blue-600"
            />
            <span className="text-sm text-slate-600 dark:text-slate-300">Top 5 per sector</span>
          </label>
        </div>
      </div>

      {/* Results */}
      {loading && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 animate-pulse">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-9 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          ))}
        </div>
      )}

      {!loading && result && !result.bySector && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-2">
            {result.results.length} matches · as of {new Date(result.asOf).toLocaleTimeString()}
          </p>
          <ResultsTable rows={result.results} onFlag={askOpus} />
        </div>
      )}

      {!loading && result?.bySector && (
        <div className="space-y-5">
          {Object.entries(result.bySector).map(([sector, rows]) => (
            <div key={sector} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">{sector}</h3>
              <ResultsTable rows={rows} onFlag={askOpus} />
            </div>
          ))}
          {Object.keys(result.bySector).length === 0 && (
            <p className="text-center text-sm text-slate-500 py-8">No equities match the current criteria.</p>
          )}
        </div>
      )}
    </ResearchShell>
  )
}
